import { AppContext, Booking, BookingData } from "../../../../AppDataProvider/AppDataProvider";
import { corners, marginBottom, marginLeft, marginRight, media, typography } from "../../../../common/styles";
import { getCurrencyDisplay, requireBookingDataFields } from "../../../../utils";
import React, { useContext } from "react";
import { applyOpacity } from "../../../../common/styles/utils";
import { Checkbox } from "../../../../common/components";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PartRequired } from "../../../../utils/types";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
type AvailabilityStepBookingData = PartRequired<BookingData, "attendees" | "duration" | "organisation" | "recurringBooking" | "service" | "timezone">;
type ItemProps = {
  booking: Booking;
  onSelectBooking: (selectedBooking: Booking) => void;
  selectedBookings: Booking[];
};
export function Item({
  booking,
  onSelectBooking,
  selectedBookings
}: ItemProps): React.ReactElement {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    appData
  } = useContext(AppContext);
  requireBookingDataFields<AvailabilityStepBookingData>(appData.bookingData, ["attendees", "organisation", "timezone", "duration", "recurringBooking"]);
  const {
    organisation,
    timezone,
    attendees,
    duration,
    recurringBooking
  } = appData.bookingData;
  const {
    recurring
  } = recurringBooking;
  const {
    bookings
  } = recurring;
  const {
    recurrenceType
  } = recurring;
  const bookingSelectionEnabled = recurrenceType == "INDIVIDUAL_BOOKINGS";
  const {
    _id,
    start,
    price,
    remainingSpaces
  } = booking;
  const startDate = new Date(start);
  const durationPrice = duration.price ?? 0;
  const showBookingPrice = bookings.some(booking => (booking.price !== undefined && booking.price !== null ? booking.price : durationPrice) > 0);
  const priceToDisplay = typeof price !== "undefined" && price !== null ? price : durationPrice;
  const totalBookingSpecificPrice = priceToDisplay * attendees;
  const totalSpecificPriceDisplay = totalBookingSpecificPrice > 0 ? getCurrencyDisplay(totalBookingSpecificPrice, organisation.currency, i18n.language) : t("free");
  const checkboxAriaLabel = new Intl.DateTimeFormat(i18n.language, /* eslint-disable sort-keys */{
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    timeZone: timezone
  } /* eslint-enable sort-keys */).format(startDate);
  const isBookingSelected = selectedBookings.includes(booking);
  const dayDisplay = new Intl.DateTimeFormat(i18n.language, {
    day: "2-digit",
    timeZone: timezone
  }).format(startDate);
  const weekdayDisplay = new Intl.DateTimeFormat(i18n.language, {
    timeZone: timezone,
    weekday: "short"
  }).format(startDate);
  const bookingStart = new Intl.DateTimeFormat(i18n.language, {
    timeStyle: "short",
    timeZone: timezone
  }).format(startDate);
  const toggleBooking = () => {
    onSelectBooking(booking);
  };
  return <ListItem onClick={toggleBooking} key={_id} data-sentry-element="ListItem" data-sentry-component="Item" data-sentry-source-file="Item.tsx">
      <Info data-sentry-element="Info" data-sentry-source-file="Item.tsx">
        <Day data-sentry-element="Day" data-sentry-source-file="Item.tsx">{dayDisplay}</Day>
        <Weekday data-sentry-element="Weekday" data-sentry-source-file="Item.tsx">{weekdayDisplay}</Weekday>
        <TimeAndSpaces data-sentry-element="TimeAndSpaces" data-sentry-source-file="Item.tsx">
          <Time data-sentry-element="Time" data-sentry-source-file="Item.tsx">
            <RecurringIcon icon={faSyncAlt} aria-label={t("recurringIconLabel")} data-sentry-element="RecurringIcon" data-sentry-source-file="Item.tsx" />
            {bookingStart}
          </Time>

          <Spaces data-sentry-element="Spaces" data-sentry-source-file="Item.tsx">
            <SpacesLabel data-sentry-element="SpacesLabel" data-sentry-source-file="Item.tsx">{t("spacesLeft")}</SpacesLabel>
            {remainingSpaces}
          </Spaces>
        </TimeAndSpaces>
      </Info>
      <PriceAndSelect data-sentry-element="PriceAndSelect" data-sentry-source-file="Item.tsx">
        {showBookingPrice && <Price>{totalSpecificPriceDisplay}</Price>}
        {bookingSelectionEnabled && <SelectBookingCheckbox aria-label={checkboxAriaLabel} checked={isBookingSelected} onChange={toggleBooking} />}
      </PriceAndSelect>
    </ListItem>;
}
const ListItem = styled.li`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  ${marginBottom.l}

  @media ${media.hoverable} {
    &:hover {
      cursor: pointer;
    }
  }
`;
const Info = styled.div`
  display: flex;
  align-items: center;
`;
const Day = styled.div`
  flex-shrink: 0;
  height: 2.5rem;
  width: 2.5rem;
  ${marginRight.s}
  ${corners.m};

  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.05, false)};
  ${typography.bodyBold};
  /*
   * Was unset — renders {dayDisplay} text directly on top of the
   * background-color wash above, with no explicit colour of its own.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const Weekday = styled.div`
  ${typography.body};
  min-width: 3rem;
  /*
   * Was unset — renders weekday text directly with no ambient colour
   * anywhere up the tree (the parent ListItem sets none either), so it
   * silently fell back to the browser default black.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const TimeAndSpaces = styled.div`
  display: flex;

  @media ${media.mobile} {
    flex-direction: column;
  }
`;
const RecurringIcon = styled(FontAwesomeIcon)`
  ${marginRight.s}
  color: ${({
  theme
}) => theme.colours.primaryText};
`;
const Time = styled.div`
  display: flex;
  ${marginRight.m}
  align-items: center;
  ${typography.body}
  /*
   * Was unset — same gap as Weekday above.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const Spaces = styled.div`
  display: flex;
  ${typography.small};
  align-items: center;
  /*
   * Was unset — renders the raw spacesLeft number directly (alongside
   * SpacesLabel, which sets its own colour), so the number itself fell
   * back to browser-default black.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const SpacesLabel = styled.div`
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6, false)};
  ${marginRight.xs}
  &::first-letter {
    text-transform: capitalize;
  }
`;
const PriceAndSelect = styled.div`
  display: flex;
  align-items: center;
`;
const Price = styled.div.attrs({
  className: "servicePrice"
})`
  ${typography.smallBold};
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6, false)};
  align-items: center;
`;
const SelectBookingCheckbox = styled(Checkbox)`
  ${marginLeft.m}
`;